import * as React from "react";
import { useDispatch, useSelector } from "@teamprevent/admin-redux";
import { Button, Loader, Modal } from "semantic-ui-react";
import { groupBy } from "lodash";

import { IsLoadingButton } from "@teamprevent/common/IsLoadingButton";
import { navigateToParentRoute } from "@teamprevent/common/routing/actions";
import { toast } from "@teamprevent/common/toasts";

import * as moment  from "moment";

import { ApplicationState } from "../../../applicationState";
import { getRecallDate, isInThePastIfProvided, SetRecallModel } from "../models";
import { recallApi } from "../recallApi";
import { CREATING_RECALLS } from "../actions";

import { RecallForm } from "./RecallForm";

export const EmployeeRecordRecalls: React.FunctionComponent = () => {
    const dispatch = useDispatch();

    const customerId = useSelector((state: ApplicationState) => state.router?.params?.customerId);
    const employeeRecordId = useSelector((state: ApplicationState) => state.router?.params?.employeeRecordId);

    const [isOpen, setIsOpen] = React.useState<boolean>(true);
    const [loading, setLoading] = React.useState(false);
    const [recalls, setRecalls] = React.useState<SetRecallModel[]>([]);

    React.useEffect(() => {
        (async () => {
            setLoading(true);
            const hsRecalls = await recallApi.getRecallOutcomesForEmployeeRecordId(customerId, employeeRecordId);
            const loadedRecalls: SetRecallModel[] =
                Object.values(groupBy(hsRecalls, o => o.ohCaseId ?? getRecallDate(o)))
                    .map(r => ({
                        recallYears: r[0].recallYears,
                        recallWeeks: r[0].recallWeeks,
                        recallDate: getRecallDate(r[0]),
                        medicalTypes: r.map(o => o.medicalType),
                        ohCaseId: r[0].ohCaseId,
                        ohCaseNumber: r[0].ohCaseNumber,
                        inflight: r[0].inflight
                    }));
            setRecalls(loadedRecalls);
            setLoading(false);
        })();
    }, []);

    const onRecallsChanged = (updatedRecalls: SetRecallModel[]) => setRecalls(updatedRecalls);

    const onCancel = () => {
        setIsOpen(false);
        dispatch(navigateToParentRoute());
    };

    const onCreateRecalls = async () => {
        if (recalls.filter(r => r.ohCaseId === null).some(r => r.medicalTypes.length === 0))
        {
            toast.warning("New recalls must contain at least 1 medical");
            return;
        }

        const recallsToUpdate = recalls.filter(r => !r.inflight);
        if (recallsToUpdate.map(r => r.recallDate).some(isInThePastIfProvided)) {
            toast.warning("Recall date can not be in the past");
            return;
        }

        setLoading(true);
        await recallApi.setEmployeeRecordRecalls(customerId, employeeRecordId, recallsToUpdate);
        setLoading(false);
        setIsOpen(false);
        dispatch(navigateToParentRoute());
    };

    const addRecall = async () => {
        if (recalls.length === 0)
        {
            toast.warning("There is no medicals available");
            return;
        }

        setRecalls([...recalls, {
            ohCaseId: null,
            recallYears: null,
            recallWeeks: null,
            recallDate: moment(),
            medicalTypes: [],
            inflight: false
        }]);
    };

    if (loading) {
        return <Loader active />;
    }

    return (
        <Modal open={isOpen}>
            <Modal.Header>Update Recalls</Modal.Header>
            <Modal.Content>
                <RecallForm
                    recalls={recalls}
                    onRecallsChanged={onRecallsChanged}
                    hideRecallYearsAndWeeks={true}
                />
            </Modal.Content>
            <Modal.Actions>
                <Button content="Cancel" onClick={onCancel} />
                <Button content="Add Recall" onClick={addRecall} />
                <IsLoadingButton requestNames={CREATING_RECALLS} content="Update Recalls" onClick={onCreateRecalls} />
            </Modal.Actions>
        </Modal>
    );
};
